import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, Medal, Crown, TrendingUp, User } from 'lucide-react';

interface LeaderboardEntry {
    _id: string;
    username: string;
    avatar?: string;
    level?: number;
    xp?: number;
    stats: {
        gamesPlayed: number;
        wins: number;
    };
}

interface LeaderboardProps {
    entries: LeaderboardEntry[];
    currentUserId: string;
}

const Leaderboard: React.FC<LeaderboardProps> = ({ entries, currentUserId }) => {
    if (entries.length === 0) {
        return (
            <div className="text-center py-12 text-cream-200/40 border-2 border-dashed border-navy-800 rounded-xl">
                <Trophy size={48} className="mx-auto mb-4 opacity-50" />
                <p>Aucun classement disponible pour le moment.</p>
            </div>
        );
    }

    const getRankIcon = (rank: number) => {
        switch (rank) {
            case 1: return <Crown size={24} className="text-gold-400 fill-gold-400" />;
            case 2: return <Medal size={24} className="text-gray-300" />;
            case 3: return <Medal size={24} className="text-amber-700" />;
            default: return <span className="font-bold font-serif text-cream-200/50">#{rank}</span>;
        }
    };

    return (
        <div className="bg-navy-900/40 backdrop-blur-sm border border-navy-700/50 rounded-xl overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-12 gap-4 p-4 border-b border-navy-700/50 bg-navy-950/30 text-xs font-bold text-gold-500/70 uppercase tracking-widest">
                <div className="col-span-1 text-center">Rang</div>
                <div className="col-span-5 md:col-span-6 pl-4">Joueur</div>
                <div className="col-span-3 md:col-span-2 text-center">Victoires</div>
                <div className="col-span-3 text-right pr-4">Ratio</div>
            </div>

            {/* list */}
            <div className="divide-y divide-navy-800/50">
                {entries.map((entry, index) => {
                    const rank = index + 1;
                    const isCurrentUser = entry._id === currentUserId;
                    const winRate = entry.stats.gamesPlayed > 0
                        ? Math.round((entry.stats.wins / entry.stats.gamesPlayed) * 100)
                        : 0;

                    return (
                        <motion.div
                            key={entry._id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className={`grid grid-cols-12 gap-4 p-4 items-center transition-colors duration-200
                                ${isCurrentUser ? 'bg-gold-500/10 border-l-2 border-gold-500' : 'hover:bg-navy-800/30'}
                            `}
                        >
                            {/* Rank */}
                            <div className="col-span-1 flex justify-center items-center">
                                {getRankIcon(rank)}
                            </div>

                            {/* Player */}
                            <div className="col-span-5 md:col-span-6 flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full border border-gold-500/30 overflow-hidden bg-navy-900 shrink-0`}>
                                    {entry.avatar ? (
                                        <img src={`/assets/avatars/${entry.avatar}`} alt={entry.username} className="w-full h-full object-cover" />
                                    ) : (
                                        <User size={16} className="m-auto text-cream-200/50 mt-1.5" />
                                    )}
                                </div>
                                <div className="truncate font-medium text-cream-100 font-serif flex items-center gap-2">
                                    {entry.username}
                                    <span className="text-xs bg-purple-500/20 text-purple-300 px-1.5 py-0.5 rounded border border-purple-500/30 shadow-[0_0_10px_rgba(168,85,247,0.2)]">
                                        Lvl {entry.level || 1}
                                    </span>
                                    {isCurrentUser && <span className="text-xs text-gold-400/60 font-sans tracking-wide">(Vous)</span>}
                                </div>
                            </div>

                            {/* Wins */}
                            <div className="col-span-3 md:col-span-2 text-center flex items-center justify-center gap-2">
                                <span className="text-cream-100 font-bold">{entry.stats.wins}</span>
                                <span className="text-xs text-cream-200/40 hidden sm:inline"> vict.</span>
                            </div>

                            {/* Win Rate */}
                            <div className="col-span-3 text-right flex items-center justify-end gap-2 pr-4">
                                <div className={`text-sm font-bold ${winRate >= 50 ? 'text-green-400' : 'text-cream-200/60'}`}>
                                    {winRate}%
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );
};

export default Leaderboard;
